import { Button } from '@liveauctioneers/hammer-ui-core/button';
import { useIntl } from '@liveauctioneers/hammer-ui-core/intl';
import { cn } from '@liveauctioneers/hammer-ui-theme/cn';
import { useCallback, useEffect, useRef, useState } from 'react';
import { Virtuoso, VirtuosoHandle } from 'react-virtuoso';

import { useAppSelector } from '@/redux/hooks';
import { getDeployment } from '@/redux/modules/config';
import { getLiveCatalogCurrentItemId } from '@/redux/modules/console';
import { getItemSummariesForCatalog } from '@/redux/modules/item/summary/itemSummary.selectors';
import { ItemSummary } from '@/types/item/ItemSummary';
import { useTrackBackToLiveClickedAnalytics } from '@/utils/amplitude/analytics/bidding/trackBackToLiveClicked.analytics';

import { ListItem } from './components/ListItem/ListItem';
import { useCalculatedRelativeFavoritesPosition } from './hooks/useCalculatedRelativeFavoritesPosition';
import { useFindLiveItemIndex } from './hooks/useFindLiveItemIndex';

type Props = {
    catalogId: number;
};

const TEST_INITIAL_ITEM_COUNT = 4;

export const AuctionLotsScroller = ({ catalogId }: Props) => {
    const itemId = useAppSelector((state) => getLiveCatalogCurrentItemId(state, catalogId));
    const items = useAppSelector((state) => getItemSummariesForCatalog(state, catalogId));
    const deployment = useAppSelector(getDeployment);
    const { formatMessage } = useIntl();
    const trackBackToLiveClicked = useTrackBackToLiveClickedAnalytics();

    // sometimes the itemSummary index isn't the same as it's position in the list so we need to find it manually
    const liveItemIndex = useFindLiveItemIndex({ catalogId, itemId });
    const [isLiveItemVisible, setIsLiveItemVisible] = useState<boolean>(true);

    // Used to see if it should autoscroll
    const [indexDifference, setIndexDifference] = useState<number>(liveItemIndex);

    const lotsUntilUpcomingFavorites = useCalculatedRelativeFavoritesPosition({
        catalogId,
        itemId,
    });

    const virtuoso = useRef<VirtuosoHandle>(null);

    const safeLiveItemIndex = Number.isFinite(liveItemIndex) ? Math.max(0, Math.trunc(liveItemIndex)) : 0;
    const topMostItemIndex = items.length > 0 ? Math.min(safeLiveItemIndex, items.length - 1) : 0;

    const handleScrollToLiveItem = useCallback(() => {
        virtuoso?.current?.scrollToIndex?.({
            align: 'start',
            behavior: 'smooth',
            index: topMostItemIndex,
        });
    }, [topMostItemIndex]);

    // Handle if react-virtuoso scrolls, track if live item is visible or not
    const handleOnRangeChanged = ({ endIndex, startIndex }) => {
        if (liveItemIndex >= startIndex && liveItemIndex <= endIndex) {
            setIsLiveItemVisible(true);
        } else {
            setIsLiveItemVisible(false);
            setIndexDifference(liveItemIndex);
        }
    };

    useEffect(() => {
        if (isLiveItemVisible && indexDifference !== liveItemIndex) {
            handleScrollToLiveItem();
            setIndexDifference(liveItemIndex);
        }
    }, [liveItemIndex, isLiveItemVisible, handleScrollToLiveItem, indexDifference]);

    return (
        <div
            className={cn(
                'relative h-full border border-border-secondary bg-background-primary',
                !isLiveItemVisible && 'border-t-0'
            )}
            data-testid="auctionLotScroller"
        >
            <div
                className={cn(
                    'absolute -left-px -right-px top-0 z-[3] flex items-center border-b border-l-[4px] border-r border-b-border-secondary border-l-background-primary border-r-background-primary bg-background-primary p-md pt-0 transition-[opacity,visibility,transform] duration-500',
                    !isLiveItemVisible ? 'visible translate-y-0' : 'pointer-events-none invisible -translate-y-full'
                )}
            >
                <Button
                    className={cn(
                        'w-full transition-opacity duration-300',
                        !isLiveItemVisible ? 'opacity-100' : 'opacity-0'
                    )}
                    onClick={() => {
                        trackBackToLiveClicked();
                        handleScrollToLiveItem();
                    }}
                    variant="outlined"
                >
                    {formatMessage({
                        id: 'bidding_console.console.button.backToLiveItem',
                    })}
                </Button>
            </div>

            <Virtuoso
                data={items}
                // Used for tests
                data-testid="virtuosoScrollList"
                initialItemCount={
                    deployment === 'test' && items.length > 0
                        ? Math.min(TEST_INITIAL_ITEM_COUNT, items.length)
                        : undefined
                }
                initialTopMostItemIndex={topMostItemIndex}
                itemContent={(_index, item: ItemSummary) => (
                    <ListItem
                        index={_index}
                        isLiveItemVisible={isLiveItemVisible}
                        itemId={item.itemId}
                        liveItemId={itemId}
                        liveItemIndex={liveItemIndex}
                        lotsUntilUpcoming={lotsUntilUpcomingFavorites[item.itemId]}
                    />
                )}
                rangeChanged={handleOnRangeChanged}
                ref={virtuoso}
            />
        </div>
    );
};
